import React from 'react';
import { Printer } from 'lucide-react';

// Playmobil × Hupalupa — Türkiye "Going to Break the Internet" Creative Idea Deck.
// Static presentation (no data fetching). Same Glowytics corporate language as the strategy deck:
// single-accent indigo + neutral slate; always light theme. All styles are scoped to `.pm-deck`.

const CSS = `
.pm-deck {
  --accent:#4f46e5; --accent-strong:#4338ca; --accent-soft:#6d66ea; --accent-mute:#c7c4f5;
  --pm-green:#2f8f80; --pm-red:#c65b6e; --pm-yellow:#b98a3e;
  --ink:#0f172a; --ink-soft:#475569; --paper:#f4f7ff; --paper-2:#ffffff;
  --line:#e4e9f7; --muted:#5b647a; --muted-2:#94a0b8;
  --bg:var(--paper); --surface:var(--paper-2); --surface-2:#eef2fd;
  --text:var(--ink); --text-dim:var(--muted); --border:var(--line);
  --grid-max:1180px; --radius:14px;
  --shadow:0 1px 2px rgba(15,23,42,.05), 0 12px 34px -20px rgba(15,23,42,.28);
  --f-sans:-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,Roboto,"Helvetica Neue",Arial,sans-serif;
  --f-mono:ui-monospace,"SF Mono","Cascadia Mono",Menlo,Consolas,monospace;
  background:var(--bg); color:var(--text); font-family:var(--f-sans); line-height:1.55;
  -webkit-font-smoothing:antialiased; min-height:100vh;
}
.pm-deck * { box-sizing:border-box; }
.pm-deck .wrap { max-width:var(--grid-max); margin:0 auto; padding:0 clamp(20px,4vw,48px); }
.pm-deck .slide { padding:clamp(48px,7vh,96px) 0; border-bottom:1px solid var(--border); position:relative; }
.pm-deck .slide:last-of-type { border-bottom:none; }
.pm-deck .eyebrow { display:inline-flex; align-items:center; gap:.55em; font-size:.74rem; font-weight:700; letter-spacing:.16em; text-transform:uppercase; color:var(--text-dim); }
.pm-deck .eyebrow .dot { width:9px; height:9px; border-radius:50%; background:var(--accent); }
.pm-deck .slide-no { font-family:var(--f-mono); font-size:.72rem; letter-spacing:.1em; color:var(--muted-2); position:absolute; top:22px; right:clamp(20px,4vw,48px); }
.pm-deck h1,.pm-deck h2,.pm-deck h3 { text-wrap:balance; margin:0; line-height:1.05; letter-spacing:-.02em; }
.pm-deck h1 { font-size:clamp(2.4rem,6vw,5rem); font-weight:800; }
.pm-deck h2 { font-size:clamp(1.8rem,4vw,3rem); font-weight:800; }
.pm-deck h3 { font-size:clamp(1.1rem,1.8vw,1.4rem); font-weight:700; letter-spacing:-.01em; }
.pm-deck p { margin:0; }
.pm-deck .lead { font-size:clamp(1.02rem,1.5vw,1.28rem); color:var(--text-dim); line-height:1.5; max-width:64ch; }
.pm-deck .section-head { margin-bottom:clamp(26px,4vh,44px); }
.pm-deck .section-head h2 { margin-top:14px; }
.pm-deck .section-head .lead { margin-top:16px; }
.pm-deck .cover { border-bottom:none; padding-top:clamp(24px,4vh,48px); }
.pm-deck .cover-top { display:flex; justify-content:space-between; align-items:center; gap:20px; flex-wrap:wrap; margin-bottom:clamp(36px,7vh,80px); }
.pm-deck .logo-lockup { display:flex; align-items:center; gap:16px; font-weight:800; letter-spacing:-.01em; font-size:1.05rem; }
.pm-deck .brick { display:inline-grid; grid-template-columns:repeat(3,11px); grid-auto-rows:11px; gap:3px; }
.pm-deck .brick i { border-radius:2px; display:block; }
.pm-deck .cover h1 .hl { color:var(--accent); }
.pm-deck .cover-sub { margin-top:26px; font-size:clamp(1.1rem,2vw,1.5rem); color:var(--text-dim); max-width:52ch; }
.pm-deck .cover-meta { margin-top:clamp(36px,6vh,64px); display:flex; flex-wrap:wrap; gap:14px 40px; padding-top:26px; border-top:2px solid var(--ink); }
.pm-deck .cover-meta div { display:flex; flex-direction:column; gap:3px; }
.pm-deck .cover-meta .k { font-size:.72rem; letter-spacing:.14em; text-transform:uppercase; color:var(--text-dim); font-weight:700; }
.pm-deck .cover-meta .v { font-size:1.05rem; font-weight:700; }
.pm-deck .rainbow-rule { height:3px; border-radius:3px; margin-top:34px; background:linear-gradient(90deg,var(--accent),color-mix(in srgb,var(--accent) 22%,transparent) 62%,transparent); }
.pm-deck .grid { display:grid; gap:18px; }
.pm-deck .g2 { grid-template-columns:repeat(2,1fr); }
.pm-deck .g3 { grid-template-columns:repeat(3,1fr); }
.pm-deck .g4 { grid-template-columns:repeat(4,1fr); }
@media (max-width:860px){ .pm-deck .g3,.pm-deck .g4 { grid-template-columns:repeat(2,1fr); } }
@media (max-width:560px){ .pm-deck .g2,.pm-deck .g3,.pm-deck .g4 { grid-template-columns:1fr; } }
.pm-deck .card { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius); padding:22px 22px 20px; box-shadow:var(--shadow); position:relative; }
.pm-deck .card.flat { box-shadow:none; }
.pm-deck .card .tag { font-size:.7rem; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:var(--text-dim); }
.pm-deck .card h3 { margin:10px 0 8px; }
.pm-deck .card p { color:var(--text-dim); font-size:.95rem; }
.pm-deck .topbar { height:4px; border-radius:4px 4px 0 0; margin:-22px -22px 18px; }
.pm-deck .c-blue { background:var(--accent); } .pm-deck .c-red { background:var(--pm-red); }
.pm-deck .c-yellow { background:var(--accent-soft); } .pm-deck .c-green { background:var(--pm-green); }
.pm-deck .c-purple { background:var(--accent-strong); }
.pm-deck .stat { display:flex; flex-direction:column; gap:4px; }
.pm-deck .stat .num { font-size:clamp(1.8rem,3.2vw,2.7rem); font-weight:800; letter-spacing:-.03em; font-variant-numeric:tabular-nums; line-height:1; }
.pm-deck .stat .lab { font-size:.86rem; color:var(--text-dim); }
.pm-deck .stat .sub { font-size:.76rem; color:var(--muted-2); }
.pm-deck ul.clean { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:10px; }
.pm-deck ul.clean li { position:relative; padding-left:26px; font-size:.96rem; color:var(--text-dim); line-height:1.45; }
.pm-deck ul.clean li::before { content:""; position:absolute; left:4px; top:.55em; width:8px; height:8px; border-radius:2px; background:var(--accent); }
.pm-deck ul.clean li b { color:var(--text); font-weight:700; }
.pm-deck .callout { border-left:4px solid var(--pm-yellow); background:color-mix(in srgb,var(--pm-yellow) 12%,var(--surface)); padding:16px 20px; border-radius:0 10px 10px 0; font-size:.95rem; }
.pm-deck .callout b { color:var(--text); }
.pm-deck .pill { display:inline-flex; align-items:center; gap:6px; font-size:.72rem; font-weight:700; letter-spacing:.04em; padding:4px 10px; border-radius:999px; border:1px solid var(--border); color:var(--text-dim); }
.pm-deck .source-note { font-size:.78rem; color:var(--muted-2); margin-top:20px; line-height:1.5; }
.pm-deck footer { padding:44px 0 60px; color:var(--text-dim); font-size:.85rem; }
.pm-deck footer .rainbow-rule { margin-bottom:24px; }
.pm-deck .toolbar { position:sticky; top:0; z-index:20; display:flex; justify-content:flex-end; gap:10px; padding:12px clamp(20px,4vw,48px); background:color-mix(in srgb,var(--bg) 88%,transparent); backdrop-filter:blur(8px); border-bottom:1px solid var(--border); }
.pm-deck .btn { display:inline-flex; align-items:center; gap:8px; font-size:.82rem; font-weight:700; padding:8px 14px; border-radius:9px; border:1px solid var(--border); background:var(--surface); color:var(--text); cursor:pointer; }
.pm-deck .btn:hover { border-color:var(--accent); color:var(--accent); }
/* Big idea hero */
.pm-deck .bigidea { background:linear-gradient(135deg,#1e1b4b,#312e81 48%,#4338ca); color:#fff; border-radius:var(--radius); padding:clamp(28px,5vw,52px); box-shadow:var(--shadow); }
.pm-deck .bigidea .kick { font-size:.74rem; letter-spacing:.16em; text-transform:uppercase; font-weight:700; color:rgba(255,255,255,.72); }
.pm-deck .bigidea h2 { color:#fff; margin:12px 0 14px; font-size:clamp(2rem,4.5vw,3.4rem); }
.pm-deck .bigidea p { color:rgba(255,255,255,.9); font-size:clamp(1rem,1.6vw,1.25rem); max-width:60ch; }
.pm-deck .bigidea .tri { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-top:26px; }
@media (max-width:680px){ .pm-deck .bigidea .tri { grid-template-columns:1fr; } }
.pm-deck .bigidea .tri > div { background:rgba(255,255,255,.09); border:1px solid rgba(255,255,255,.16); border-radius:12px; padding:18px; }
.pm-deck .bigidea .tri h4 { margin:0 0 6px; font-size:.95rem; color:#fff; }
.pm-deck .bigidea .tri p { font-size:.88rem; color:rgba(255,255,255,.82); }
/* Idea card */
.pm-deck .idea .ix { position:absolute; top:16px; right:18px; font-family:var(--f-mono); font-size:.8rem; font-weight:700; color:var(--muted-2); }
.pm-deck .idea h3 { padding-right:34px; }
.pm-deck .idea .viral { margin-top:12px; padding-top:11px; border-top:1px dashed var(--border); font-size:.85rem; color:var(--text-dim); }
.pm-deck .idea .viral b { color:var(--accent-strong); font-weight:700; }
/* Viral flywheel flow */
.pm-deck .flow { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin-top:6px; }
.pm-deck .flow .step { flex:1 1 140px; min-width:120px; background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:14px 14px; box-shadow:var(--shadow); }
.pm-deck .flow .step .n { font-family:var(--f-mono); font-size:.7rem; color:var(--muted-2); }
.pm-deck .flow .step .t { font-weight:700; font-size:.92rem; margin-top:3px; }
.pm-deck .flow .arw { color:var(--accent); font-weight:800; font-size:1.1rem; }
@media (max-width:640px){ .pm-deck .flow .arw { display:none; } }
@media print {
  .pm-deck .toolbar { display:none; }
  .pm-deck .slide { break-inside:avoid; page-break-inside:avoid; }
}
`;

// Idea-heavy slides (03–09). Each card: h=hook, m=mechanic, v=why it's viral.
const COLORS = ['c-blue', 'c-red', 'c-green', 'c-yellow', 'c-purple'];
const IDEA_SLIDES = [
  {
    no: '03', eyebrow: 'Content Bomb', title: 'The "Türkiye in Playmobil" universe',
    lead: 'The brand operates like a content studio, not a toy: it produces a shareable moment every week through stop-motion & diorama series that miniaturize Türkiye.',
    cards: [
      { h: 'That moment, in Playmobil', m: 'A reactive content desk that recreates the trending viral Turkish moment (sports victory, awards show, meme, debate) as a diorama/stop-motion piece within 24 hours.', v: 'Real-time marketing; carries news value, spreads like a meme.' },
      { h: 'The "Neighborhood" series', m: 'Corner shop, coffeehouse, apartment building, dolmuş (shared taxi), tea garden — Turkish neighborhood life recreated in miniature sets. Warm, nostalgic, familiar.', v: 'Cultural belonging: makes people say "that\'s exactly it," gets quoted and shared.' },
      { h: 'TV & film universe', m: 'Recreate iconic scenes from beloved shows and films with Playmobil (within a parody/general-culture framing).', v: 'Fandom sharing + the power of cultural reference.' },
      { h: 'Weekly mini-series', m: 'Dub Novelmore into Turkish with local characters in mini episodes; a format that keeps viewers coming back regularly.', v: 'Consistency = algorithm favor + community loyalty.' },
    ],
  },
  {
    no: '04', eyebrow: 'Timing Opportunity', title: 'The WWE launch = a cultural bomb',
    lead: 'The Playmobil WWE series launches in July 2026, and "wwe" gets ~27,000 monthly searches in Türkiye. We turn this launch into the year\'s most-talked-about toy moment.',
    cards: [
      { h: 'Playmobil WrestleMania', m: 'Stop-motion mini matches shot with the figures; a ring diorama, announcer narration, a weekly "who wins?" series with voting.', v: 'A strong WWE cult following in Türkiye + a series format = repeat viewing.' },
      { h: 'Giant Playmobil ring', m: 'A life-size Playmobil wrestling ring installation at a mall/event; cosplay, meet & greet, photo spot.', v: 'A physical spectacle = press coverage + mass UGC.' },
      { h: 'Streamer & comedian league', m: 'A "Playmobil Wrestling League" with Turkish creators — kayfabe storylines, a season-long championship.', v: 'Creator audiences + competitive drama fuel virality.' },
      { h: 'Limited figure drop', m: 'A figure chosen by community vote with VIP early access; a countdown, limited-stock "drop" mechanic.', v: 'FOMO + collectibility; sneaker-drop energy.' },
    ],
  },
  {
    no: '05', eyebrow: 'Q4 Tentpole', title: 'Playmobil Winter Village',
    lead: 'In Spain, the "belén" (New Year scene) tradition explodes every December. We bring the same ritual to Türkiye: the tradition of setting up New Year\'s with Playmobil, plus a physical spectacle.',
    cards: [
      { h: 'Giant Winter Village installation', m: 'A walkable giant Playmobil winter village in an Istanbul square/mall — live dioramas, photo spots, shoppable QR codes.', v: 'An Instagrammable landmark; becomes a city talking point.' },
      { h: '24-day Advent challenge', m: 'A new scene/clue/gift every day; users share their own advent scenes, with a daily giveaway.', v: 'Daily repeat engagement + UGC + buzz that lasts the whole season.' },
      { h: '"Which set is right for you?" quiz', m: 'A New Year gift guide + personality quiz → personalized set recommendation + cart.', v: 'A shareable result + a direct bridge to sales.' },
      { h: 'The New Year\'s table ritual', m: 'A "build your own New Year scene" call to action; a mini winter set + template for families.', v: 'Creating a new tradition = a recurring annual brand moment.' },
    ],
  },
  {
    no: '06', eyebrow: 'Kidult & Nostalgia', title: 'Türkiye-exclusive collector moments',
    lead: 'The adult collector (kidult) market is large and highly newsworthy. Türkiye-exclusive limited sets and a nostalgia wave designed to generate PR buzz.',
    cards: [
      { h: 'Türkiye-exclusive limited set', m: '"Turkish Neighborhood / Dolmuş / Corner Shop / Tea Garden" concept sets (negotiate with production headquarters; at minimum as a prototype/PR product).', v: 'A press bombshell + collector FOMO + local pride.' },
      { h: '"Rebuild your childhood"', m: '\'90s/2000s nostalgia; kidult unboxing, a collector community, and swap events.', v: 'Emotional nostalgia is the most-shared trigger.' },
      { h: 'Desktop collection', m: 'Position the Vespa/Porsche/Ferrari licensed figures as an "office/desktop adult toy."', v: 'A new audience + gifting occasion; penetrates the business world.' },
      { h: 'Artist collab figure', m: 'A custom-designed figure/diorama with a well-known Turkish illustrator/sculptor, released as a signed limited series.', v: 'Art + brand = coverage in culture pages.' },
    ],
  },
  {
    no: '07', eyebrow: 'Community Engine', title: 'The creator & UGC machine',
    lead: 'Virality that scales doesn\'t come from ads — it comes from people\'s content. We build an engine that turns users into content creators.',
    cards: [
      { h: '#BuildYourOwnTürkiye', m: 'Everyone builds their own miniature Türkiye scene (neighborhood, match day, holiday); the best ones get featured on the brand\'s channel + rewarded.', v: 'A low-cost, self-growing flood of UGC.' },
      { h: 'Tiered creator program', m: 'Mega, micro, stop-motion artists, and parent creators; a dedicated brief and content calendar for each tier.', v: 'Simultaneous organic reach across different audiences.' },
      { h: '"Creator Box"', m: 'Send selected families a content-template kit + a set; frictionless "shoot and share."', v: 'A ready-made format = a high participation rate.' },
      { h: 'Creator affiliate', m: 'A dedicated discount code for each creator + a sales-based commission (referral channel).', v: 'Ties content directly to sales; performance-based.' },
    ],
  },
  {
    no: '08', eyebrow: 'Phygital', title: 'Physical × digital bridges',
    lead: 'Participation mechanics that carry curiosity from the screen to the street, the store, and the home — turning the brand into an experience people actually try.',
    cards: [
      { h: 'AR play world', m: 'An AR filter that places a Playmobil world in your room, plus a "which Playmobil character are you?" lens.', v: 'A native format people try and share on social media.' },
      { h: 'City-wide treasure hunt', m: 'Mini figures with QR codes are hidden around the city; clues are posted on social media, and whoever finds them wins (a city-by-city wave).', v: 'A real-world game = local press coverage + excitement.' },
      { h: 'In-store play world', m: 'Touch-screen "play world" stations at dealers/retail chains, plus a "nearest store" digital bridge.', v: 'Experience → purchase; an omnichannel effect.' },
      { h: 'Sampling partnership', m: 'The McDonald\'s model: a mini-figure giveaway campaign with a QSR/grocery partner.', v: 'Millions of touchpoints; puts the brand directly in kids\' hands.' },
    ],
  },
  {
    no: '09', eyebrow: 'Purpose & PR', title: 'The screen-free play movement',
    lead: 'A purpose platform that wins parents\' hearts and trust and generates earned media — it sells the value, not the product.',
    cards: [
      { h: 'The "Screen-Free Sunday" movement', m: 'A weekly call for screen-free creative play; pediatrician/school partnerships + a manifesto + a toolkit.', v: 'Social good = strong earned media & trust.' },
      { h: 'Sustainability story', m: 'The Wiltopia + recycled-plastic narrative; donating sets to schools/underprivileged children.', v: 'A parent\'s purchase justification + goodwill.' },
      { h: 'Expert content series', m: 'Content about "the power of play" with child development experts/psychologists.', v: 'Authority + trust; shared within the parenting community.' },
      { h: '"My First Toy"', m: 'Social responsibility: first Playmobil sets for children in need, with transparent reporting.', v: 'Brand love + press coverage + employee pride.' },
    ],
  },
];

export default function PlaymobilViral() {
  return (
    <div className="pm-deck">
      <style>{CSS}</style>

      <div className="toolbar">
        <button className="btn" onClick={() => window.print()}>
          <Printer size={15} /> PDF / Print
        </button>
      </div>

      {/* ============ 01 · COVER ============ */}
      <section className="slide cover">
        <span className="slide-no">01 / 10</span>
        <div className="wrap">
          <div className="cover-top">
            <div className="logo-lockup">
              <span className="brick" aria-hidden="true">
                <i style={{ background: 'var(--accent)' }} /><i style={{ background: 'color-mix(in srgb,var(--accent) 55%,transparent)' }} /><i style={{ background: 'color-mix(in srgb,var(--accent) 30%,transparent)' }} />
                <i style={{ background: 'color-mix(in srgb,var(--accent) 55%,transparent)' }} /><i style={{ background: 'var(--accent)' }} /><i style={{ background: 'color-mix(in srgb,var(--accent) 55%,transparent)' }} />
                <i style={{ background: 'color-mix(in srgb,var(--accent) 30%,transparent)' }} /><i style={{ background: 'color-mix(in srgb,var(--accent) 55%,transparent)' }} /><i style={{ background: 'var(--accent)' }} />
              </span>
              PLAYMOBIL <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>× Hupalupa</span>
            </div>
            <div className="pill">Creative Idea Deck · Glowytics</div>
          </div>

          <span className="eyebrow"><span className="dot" />Türkiye · Viral Growth & Cultural Impact Ideas</span>
          <h1>We're going to get Türkiye<br />playing with <span className="hl">Playmobil</span>.</h1>
          <p className="cover-sub">Not an ad — a cultural moment. This deck is full of big ideas that will turn Playmobil into the brand Türkiye talks about, shares, and copies.</p>

          <div className="rainbow-rule" />

          <div className="cover-meta">
            <div><span className="k">Purpose</span><span className="v">Viral reach + cultural impact</span></div>
            <div><span className="k">Tone</span><span className="v">Bold · playful · shareable</span></div>
            <div><span className="k">Scope</span><span className="v">Content · activation · community · PR</span></div>
            <div><span className="k">Prepared by</span><span className="v">Glowytics · July 2026</span></div>
          </div>
        </div>
      </section>

      {/* ============ 02 · WHY NOW + BIG IDEA ============ */}
      <section className="slide">
        <span className="slide-no">02 / 10</span>
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow"><span className="dot" />Why Now & The Big Idea</span>
            <h2>A ready, powerful brand —<br />and an empty stage waiting.</h2>
            <p className="lead">Playmobil brings its global "The World Is Your Playground" platform, a stacked licensing portfolio (WWE, Barbie, Miraculous), and 50 years of heritage to the table. In Türkiye, though, the digital narrative is still quiet. The first bold mover wins — and taking a brand viral from scratch is also the best showcase for Glowytics.</p>
          </div>

          <div className="bigidea">
            <span className="kick">Campaign Platform</span>
            <h2>"Build Your Own Türkiye."</h2>
            <p>The Türkiye adaptation of the global "Build Your Own World" idea. Everyone builds their own Türkiye with Playmobil: their neighborhood, match day, New Year's table, favorite TV scene. One umbrella idea that ties together content, UGC, activation, and sales.</p>
            <div className="tri">
              <div><h4>What is the platform?</h4><p>A creative call to action, open to everyone, that turns Turkish culture into Playmobil scenes.</p></div>
              <div><h4>Why does it work?</h4><p>Nostalgia + belonging + play; makes people say "that's exactly it," gets shared, gets copied.</p></div>
              <div><h4>How does it scale?</h4><p>Brand content sparks it → the community multiplies it → earned media amplifies it → sales harvest it.</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 03–09 · IDEA SLIDES ============ */}
      {IDEA_SLIDES.map((s) => (
        <section className="slide" key={s.no}>
          <span className="slide-no">{s.no} / 10</span>
          <div className="wrap">
            <div className="section-head">
              <span className="eyebrow"><span className="dot" />{s.eyebrow}</span>
              <h2>{s.title}</h2>
              <p className="lead">{s.lead}</p>
            </div>
            <div className="grid g2">
              {s.cards.map((c, i) => (
                <div className="card idea" key={i}>
                  <div className={`topbar ${COLORS[i % COLORS.length]}`} />
                  <span className="ix">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{c.h}</h3>
                  <p>{c.m}</p>
                  <p className="viral"><b>⚡ Viral hook —</b> {c.v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ============ 10 · FLYWHEEL + KPI + GLOWYTICS ============ */}
      <section className="slide">
        <span className="slide-no">10 / 10</span>
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow"><span className="dot" />Viral Flywheel · Measurement · Why Glowytics</span>
            <h2>Not an idea — a system:<br />a self-reinforcing flywheel.</h2>
            <p className="lead">These ideas don't work in isolation — they work as a loop that feeds itself. Glowytics builds and optimizes this loop end to end.</p>
          </div>

          <div className="flow">
            {[
              ['01', 'Spark content'], ['02', 'UGC & creators'], ['03', 'Earned media / PR'],
              ['04', 'Brand-search growth'], ['05', 'Sales & VIP'], ['06', 'Community'],
            ].map((st, i, a) => (
              <React.Fragment key={st[0]}>
                <div className="step"><div className="n">{st[0]}</div><div className="t">{st[1]}</div></div>
                {i < a.length - 1 && <span className="arw">→</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="grid g3" style={{ marginTop: 22 }}>
            <div className="card"><div className="topbar c-blue" /><span className="tag">Measurement</span><h3>Virality KPIs</h3>
              <ul className="clean" style={{ marginTop: 10 }}>
                <li>Reach & impressions · video view-through rate</li>
                <li><b>UGC volume</b> & #hashtag usage</li>
                <li>Earned media value (PR/AVE)</li>
                <li>Brand-search volume growth · followers</li>
              </ul>
            </div>
            <div className="card"><div className="topbar c-green" /><span className="tag">Bridge to sales</span><h3>Business outcome</h3>
              <ul className="clean" style={{ marginTop: 10 }}>
                <li>Site + marketplace sales & ROAS</li>
                <li>VIP/CRM list growth</li>
                <li>New-customer rate · basket value</li>
                <li>Q4 harvest & repeat purchase</li>
              </ul>
            </div>
            <div className="card"><div className="topbar c-purple" /><span className="tag">Why us</span><h3>Glowytics's role</h3>
              <ul className="clean" style={{ marginTop: 10 }}>
                <li><b>Data → insight → big idea</b></li>
                <li>Production + creator network</li>
                <li>Media planning & optimization</li>
                <li>One team end to end, one point of accountability</li>
              </ul>
            </div>
          </div>

          <div className="callout" style={{ marginTop: 24 }}>
            <b>Goal →</b> Türkiye's <b>most-talked-about toy brand</b> within 6 months. We built this deck on data (search volume, keyword discovery, theme-fit analysis) — and we'll build the execution end to end too. Playmobil is ready; let's bring the house down.
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="rainbow-rule" />
          <strong>PLAYMOBIL × Hupalupa</strong> · Türkiye Creative Idea Deck · Prepared by: Glowytics · July 2026 · <span style={{ color: 'var(--muted-2)' }}>Internal presentation draft — ideas are conceptual and subject to production/licensing approval.</span>
        </div>
      </footer>
    </div>
  );
}
